import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calculator, Lock, Unlock, RefreshCw, AlertTriangle, CheckCircle2, Loader2, Download, Wallet } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AttendanceCalculation() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [running, setRunning] = useState(false);
  const [calcs, setCalcs] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [lock, setLock] = useState({ locked: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        const cs = Array.isArray(data) ? data : (data.companies || []);
        setCompanies(cs);
        if (cs.length === 1) setCompanyId(cs[0].id);
      } catch { /* */ }
    })();
  }, []);

  const refresh = useCallback(async () => {
    if (!companyId || !month) return;
    setLoading(true);
    try {
      const [c, e, l] = await Promise.all([
        axios.get(`${API_URL}/attendance/v2/calculations?company_id=${companyId}&month=${month}`, { withCredentials: true }),
        axios.get(`${API_URL}/attendance/v2/reports/exceptions?company_id=${companyId}&month=${month}`, { withCredentials: true }),
        axios.get(`${API_URL}/attendance/v2/lock/status?company_id=${companyId}&month=${month}`, { withCredentials: true }),
      ]);
      setCalcs(c.data.calculations || []);
      setExceptions(e.data.exceptions || []);
      setLock(l.data || { locked: false });
    } catch (err) { /* */ } finally { setLoading(false); }
  }, [companyId, month]);

  useEffect(() => { if (companyId && month) refresh(); }, [companyId, month, refresh]);


  const runCalc = async () => {
    if (!companyId || !month) { toast.error('Pick company + month'); return; }
    setRunning(true);
    try {
      const [yearStr, monthStr] = month.split('-');
      const fd = new FormData();
      fd.append('company_id', companyId);
      fd.append('month', monthStr || month);
      fd.append('year', yearStr);
      const { data } = await axios.post(`${API_URL}/attendance/v2/calculate`, fd, { withCredentials: true });
      toast.success(`Calculated ${data.employees_calculated} employees · ${data.exceptions_count} exceptions`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Calculation failed'); }
    finally { setRunning(false); }
  };

  const toggleLock = async () => {
    const path = lock.locked ? 'unlock' : 'lock';
    try {
      await axios.post(`${API_URL}/attendance/v2/${path}`,
        { company_id: companyId, month }, { withCredentials: true });
      toast.success(lock.locked ? 'Unlocked' : 'Locked for payroll');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const exportCSV = () => {
    if (!calcs.length) return;
    const headers = ['Employee Code', 'Name', 'Department', 'Calendar Days', 'Working Days',
      'Present', 'OD', 'Effective Present', 'Weekly Off', 'Holidays', 'Half Days',
      'Leave', 'Regularised', 'Comp Off', 'Absent', 'Final LOP', 'Payable Days', 'Remarks'];
    const rows = calcs.map(c => [
      c.employee_code, c.employee_name, c.department || '',
      c.calendar_days, c.working_days, c.present_days, c.od_days || 0,
      c.effective_present || (c.present_days + (c.od_days || 0)),
      c.weekly_off, c.holidays, c.half_days, c.leave_days,
      c.regularised_days || 0, c.comp_off_days || 0,
      c.absent_days, c.final_lop || c.lop_days, c.payable_days, c.remarks || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `attendance_summary_${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const totals = calcs.reduce((acc, c) => ({
    present: acc.present + (c.present_days || 0),
    od: acc.od + (c.od_days || 0),
    effective: acc.effective + (c.effective_present || ((c.present_days || 0) + (c.od_days || 0))),
    absent: acc.absent + (c.absent_days || 0),
    leave: acc.leave + (c.leave_days || 0),
    lop: acc.lop + (c.final_lop || c.lop_days || 0),
    payable: acc.payable + (c.payable_days || 0),
  }), { present: 0, od: 0, effective: 0, absent: 0, leave: 0, lop: 0, payable: 0 });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="attendance-calc-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Calculator className="text-blue-600" size={28} /> Attendance Calculation (21st–20th Cycle)
        </h1>
        <p className="text-gray-600 mt-1">Run 21st–20th cycle monthly calculation, review OD &amp; regularisation, lock for payroll.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex items-center gap-3 flex-wrap">
        <select value={companyId} onChange={e => setCompanyId(e.target.value)}
          data-testid="calc-company"
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
          <option value="">— company —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          data-testid="calc-month"
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
        <button onClick={runCalc} disabled={running || lock.locked}
          data-testid="calc-run"
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1">
          {running ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Run Calculation
        </button>
        {calcs.length > 0 && (
          <>
            <button onClick={exportCSV} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm flex items-center gap-1">
              <Download size={14} /> Export CSV
            </button>
            <a
              href={`/leave-balances?company_id=${companyId}&cycle=${month}`}
              className="px-4 py-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-sm font-semibold border border-emerald-200 flex items-center gap-1.5"
            >
              <Wallet size={14} /> Open Leave Balance ({month})
            </a>
          </>
        )}
        <span className="flex-1" />
        {lock.payroll_processed && <span className="text-xs text-red-700 font-medium">Payroll already processed</span>}
        <button onClick={toggleLock} disabled={lock.payroll_processed}
          data-testid="calc-lock-toggle"
          className={`px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1 ${lock.locked ? 'bg-amber-600' : 'bg-emerald-600'}`}>
          {lock.locked ? <><Unlock size={14} /> Unlock</> : <><Lock size={14} /> Lock for Payroll</>}
        </button>
      </div>

      {lock.locked && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 mb-4 flex items-center gap-2 text-sm">
          <CheckCircle2 size={16} className="text-emerald-700" />
          <span><b>Locked</b> for payroll{lock.locked_by_name ? ` by ${lock.locked_by_name}` : ''}{lock.locked_at ? ` at ${new Date(lock.locked_at).toLocaleString()}` : ''}.</span>
        </div>
      )}

      {calcs.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
          <KPI label="Present" value={totals.present} color="emerald" />
          <KPI label="OD (On Duty)" value={totals.od} color="blue" />
          <KPI label="Effective Present" value={totals.effective} color="amber" />
          <KPI label="Leave" value={totals.leave} color="blue" />
          <KPI label="Absent" value={totals.absent} color="red" />
          <KPI label="Final LOP" value={totals.lop} color="rose" />
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
        <div className="px-4 py-2 bg-slate-50 text-xs uppercase text-slate-600 font-medium">21st–20th Cycle Calculation Output</div>
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>
          : calcs.length === 0 ? <div className="p-10 text-center text-slate-500">Run calculation to populate this table.</div>
            : (
              <div className="overflow-x-auto max-h-[60vh]">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0 text-[11px]">
                    <tr>
                      <th className="text-left px-2 py-2">Code</th>
                      <th className="text-left px-2 py-2">Name</th>
                      <th className="text-left px-2 py-2">Dept</th>
                      <th className="text-right px-2 py-2">Calendar</th>
                      <th className="text-right px-2 py-2">Working</th>
                      <th className="text-right px-2 py-2 bg-emerald-50">Present</th>
                      <th className="text-right px-2 py-2 bg-blue-50">OD</th>
                      <th className="text-right px-2 py-2 bg-teal-50 font-bold">Eff. Present</th>
                      <th className="text-right px-2 py-2">WO</th>
                      <th className="text-right px-2 py-2">Hol</th>
                      <th className="text-right px-2 py-2 bg-amber-50">Half</th>
                      <th className="text-right px-2 py-2 bg-purple-50">Leave</th>
                      <th className="text-right px-2 py-2 bg-indigo-50">Reg</th>
                      <th className="text-right px-2 py-2 bg-sky-50">CompOff</th>
                      <th className="text-right px-2 py-2 bg-red-50">Absent</th>
                      <th className="text-right px-2 py-2 bg-rose-50 font-bold">Final LOP</th>
                      <th className="text-right px-2 py-2 bg-yellow-50 font-bold">Payable</th>
                      <th className="text-left px-2 py-2">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calcs.map(c => (
                      <tr key={c.employee_id} className="border-t border-slate-100 hover:bg-slate-50/80 transition-colors" data-testid={`calc-row-${c.employee_code}`}>
                        <td className="px-2 py-1 font-mono text-blue-700">{c.employee_code}</td>
                        <td className="px-2 py-1 font-medium">{c.employee_name}</td>
                        <td className="px-2 py-1 text-slate-500">{c.department || ''}</td>
                        <td className="px-2 py-1 text-right">{c.calendar_days}</td>
                        <td className="px-2 py-1 text-right">{c.working_days}</td>
                        <td className="px-2 py-1 text-right text-emerald-700 font-medium">{c.present_days}</td>
                        <td className="px-2 py-1 text-right text-blue-700 font-medium">{c.od_days || 0}</td>
                        <td className="px-2 py-1 text-right text-teal-800 font-bold bg-teal-50/40">{c.effective_present || ((c.present_days || 0) + (c.od_days || 0))}</td>
                        <td className="px-2 py-1 text-right">{c.weekly_off}</td>
                        <td className="px-2 py-1 text-right">{c.holidays}</td>
                        <td className="px-2 py-1 text-right">{c.half_days}</td>
                        <td className="px-2 py-1 text-right">{c.leave_days}</td>
                        <td className="px-2 py-1 text-right text-indigo-700">{c.regularised_days || 0}</td>
                        <td className="px-2 py-1 text-right text-sky-700">{c.comp_off_days || 0}</td>
                        <td className="px-2 py-1 text-right text-red-700">{c.absent_days}</td>
                        <td className="px-2 py-1 text-right text-rose-700 font-bold">{c.final_lop || c.lop_days}</td>
                        <td className="px-2 py-1 text-right font-bold text-amber-700">{c.payable_days}</td>
                        <td className="px-2 py-1 text-slate-500">{c.remarks || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>

      {exceptions.length > 0 && (
        <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
          <div className="px-4 py-2 bg-red-50 text-xs uppercase text-red-700 font-medium flex items-center gap-2">
            <AlertTriangle size={14} /> Exception Report ({exceptions.length})
          </div>
          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-xs">
              <thead className="bg-red-50">
                <tr>
                  <th className="text-left px-3 py-2">Employee</th>
                  <th className="text-left px-3 py-2">Date / Punch</th>
                  <th className="text-left px-3 py-2">Type</th>
                </tr>
              </thead>
              <tbody>
                {exceptions.map((e, i) => (
                  <tr key={i} className="border-t border-red-100">
                    <td className="px-3 py-1">{e.employee_code ? `${e.employee_code} · ${e.employee_name}` : (e.device_user_id ? `Device ${e.device_user_id}` : '—')}</td>
                    <td className="px-3 py-1">{e.date || e.punch_time || '—'}</td>
                    <td className="px-3 py-1 capitalize">{(e.type || '').replace(/_/g, ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, color = 'slate' }) {
  const palette = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
  }[color] || 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`border rounded-xl p-3 ${palette}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </div>
  );
}
